import { formatNumber } from '@/lib/formatters';
import { cn } from '@/lib/utils';

const TONE = {
  primary: 'bg-primary text-primary-foreground',
  danger: 'bg-destructive text-white',
  muted: 'bg-muted-foreground/10 text-foreground',
} as const;

const SIZE = {
  sm: 'h-4 min-w-4 px-1 text-3xs',
  default: 'h-5 min-w-5 px-1.5 text-2xs',
} as const;

export type CountBadgeProps = {
  count: number;
  /** Above this it shows "max+" (9 → "9+"). No cap when not given. */
  max?: number;
  /** primary (unread, in a basket; the default), danger (alerts), muted (a tab's or filter's count). */
  tone?: keyof typeof TONE;
  /** sm 16px (on an icon button, in a tab; the default), default 20px (a drag stack, beside a label). */
  size?: keyof typeof SIZE;
  /**
   * On the top-right corner of its parent (which needs `relative`), as on a bell. The parent's
   * aria-label says the count ("Notifications, 3 unread"), so the bubble is hidden from screen readers.
   */
  corner?: boolean;
  /** Show 0 instead of nothing. */
  showZero?: boolean;
  className?: string;
};

/**
 * A small rounded count: unread notifications on a bell, items in a basket, a tab's count. Shows
 * nothing at 0 unless `showZero`. The count (and `max`) show in the team's number format.
 */
export function CountBadge({
  count,
  max,
  tone = 'primary',
  size = 'sm',
  corner = false,
  showZero = false,
  className,
}: CountBadgeProps) {
  if (count <= 0 && !showZero) return null;
  return (
    <span
      aria-hidden={corner || undefined}
      className={cn(
        'inline-flex shrink-0 items-center justify-center rounded-full font-semibold leading-none tabular-nums',
        TONE[tone],
        SIZE[size],
        corner && 'pointer-events-none absolute -top-1 -right-1 z-10',
        className
      )}
    >
      {max != null && count > max ? `${formatNumber(max)}+` : formatNumber(count)}
    </span>
  );
}
